// src/AuthContext.tsx
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { signOut as amplifySignOut, getCurrentUser, fetchUserAttributes } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { getUser } from './graphql/queries';
import { createUser, updateUser } from './graphql/mutations';
import { generateClient } from 'aws-amplify/api';
import { StorageTier } from './API';

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: any | null;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [user, setUser] = useState<any | null>(null);

  useEffect(() => {
    checkUserAuth();

    const listener = Hub.listen('auth', (data) => {
      const { payload } = data;
      console.log("Payload in AithContext: ", payload);
      if (payload.event === 'signedIn') {
        // Check for stored redirect URL after OAuth sign-in
        const storedRedirect = localStorage.getItem('authRedirectUrl');
        if (storedRedirect && window.location.pathname === '/login') {
          window.location.href = storedRedirect;
        } else {
          checkUserAuth();
        }
      } else if (payload.event === 'signedOut') {
        setUser(null);
        setIsAuthenticated(false);
      }
    });

    return () => listener();
  }, []);

  const checkUserAuth = async () => {
    try {
      const currentUser = await getCurrentUser();
      const userAttrs = await fetchUserAttributes();
      console.log("Got user: ", currentUser, "; UserAttrs: ", userAttrs);
      const client = generateClient();
      const userName = userAttrs.name ||
        userAttrs.preferred_username ||
        userAttrs.given_name ||
        currentUser.username;
      // Check if user exists in DynamoDB
      let userResult: any;
      try {
        //get user from database
        userResult = await client.graphql({
          query: getUser,
          variables: { id: currentUser.userId },
          authMode: "userPool"
        });

        // If user doesn't exist in DynamoDB, create them
        if (!userResult.data.getUser) {
          userResult = {
            id: currentUser.userId,
            name: userName,
            email: userAttrs.email,
            phoneNumber: userAttrs.phone_number,
            storageTier: StorageTier.VIEWER
          };
          await client.graphql({
            query: createUser,
            variables: {
              input: userResult
            },
            authMode: "userPool"
          });
          userResult = await client.graphql({
            query: getUser,
            variables: { id: currentUser.userId },
            authMode: "userPool"
          });
        } else if (userResult.data.getUser.name !== userName) {
          // Update user name if it's different from OAuth name
          await client.graphql({
            query: updateUser,
            variables: {
              input: {
                id: currentUser.userId,
                name: userName,
              }
            },
            authMode: "userPool"
          });
        }
        setUser({
          ...currentUser,
          ...userAttrs,
          username: currentUser.username,
          userId: currentUser.userId,
          banjaraUser: userResult.data.getUser
        });
      } catch (userError) {
          //Ignore
      } 
      setIsAuthenticated(true);
    } catch (error) {
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  };

  const signOut = async () => {
    try {
      await amplifySignOut({ global: false });
      setUser(null);
      setIsAuthenticated(false);
      window.location.href = '/';
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, isLoading, user, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};